import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * Uputstvo za članove, srpski (4b step 4). Same content as en.tsx, the same words as the app's
 * Serbian texts (sr.tsx): "ti" without grammatical gender, "pristupni ključ", zapečaćena /
 * zaključan / blokirana. Keep it in step with the English source.
 */
export function GuideSr({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Kako Vrelis radi</h1>
      <p className="lead">
        Vrelis je servis za slanje šifrovanih poruka, vremenskih kapsula, koje mogu da se pročitaju tek od trenutka koji izabereš: svi u pilotu, ili jedna
        osoba. Pre tog trenutka niko ne može da pročita kapsulu: ni osoba kojoj pišeš, ni oni koji vode Vrelis, ni ti.
      </p>
      <p className="warn">
        Ovo je zatvoreni pilot. Preuzmi kapsule koje želiš da sačuvaš (vidi niže) i čuvaj svoju frazu za oporavak.
      </p>

      <h2>Zašto bravi može da se veruje</h2>
      <p>
        Brava nije obećanje Vrelis-a. Ona je javno objavljena kriptografija plus javni vremenski signal koji Vrelis ne kontroliše. Evo šta je drži, i u
        šta bi trebalo da poveruješ da bi popustila.
      </p>

      <h3>drand: javni sat koji potpisuje vreme</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> je mreža koju vodi{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>, grupa nezavisnih organizacija (među njima Cloudflare, Protocol Labs, Kudelski Security, EPFL
          i University College London) koje zajedno svake 3 sekunde objavljuju slučajnu, potpisanu vrednost. Vrednosti su numerisane: runda 1, runda 2 i
          tako dalje, svaka za poznat trenutak. Svako može da ih čita sa više javnih relejnih servera; buduću niko ne može da pročita, jer još ne postoji.
        </li>
        <li>
          Svaka runda je potpisana pragovnom kriptografijom (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">BLS potpisi</Ext>): svaka organizacija drži po jedan deo tajnog ključa, a za potpis je potrebna
          većina delova. Nijedna organizacija sama, ni manjina njih, ne može da napravi potpis runde pre njenog vremena. Mreža radi od 2020; lanac koji
          koristimo, <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, od
          2023. Njegov javni ključ je upisan u naš kod i svaki signal koji primimo proverava se u odnosu na njega.
        </li>
        <li>
          <b>Šifrovanje vremenskom bravom</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, koje su 2023. objavili istraživači iz Protocol
          Labs-a i Cloudflare-a) pretvara taj sat u bravu: poruka
          se šifruje za broj runde, a potpis te runde, kad bude objavljen, jedini je ključ koji je otvara. Dok ga drand ne objavi, taj ključ ne postoji
          nigde na svetu.
        </li>
      </ul>

      <h3>Kako ga Vrelis koristi</h3>
      <ul>
        <li>
          Tvoj browser šifruje tekst i ključeve tvojih fajlova za drand rundu vremena koje izabereš; strana Piši pokazuje broj runde pre slanja. Privatna
          kapsula se šifruje još jednom, ključem primaoca, pa je posle vremena može otvoriti samo on.
        </li>
        <li>
          Naš server prima samo zapečaćen rezultat i čuva ga. Nema ključ koji otvara kapsulu: ni pre vremena, a privatnu ni posle njega. Curenje baze bi
          otkrilo šifrat i metapodatke, nikad poruku.
        </li>
        <li>
          Kad vreme dođe, tvoj browser (ili offline otvarač) preuzima potpis runde sa jednog od četiri javna drand releja, proverava ga u odnosu na upisani
          javni ključ i dešifruje. Vrelis u tom koraku ne učestvuje.
        </li>
        <li>
          Svaku kapsulu potpisuje ključ autora (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, ista vrsta ključa koju koriste Bitcoin i{" "}
          <Ext href="https://nostr.com">Nostr</Ext>), pa se izmenjena kapsula prepoznaje. Ključ nastaje u tvom browser-u iz tvojih 12 reči (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) i nikad ga ne napušta nešifrovan.
        </li>
      </ul>

      <h3>Ako Vrelis nestane</h3>
      <ul>
        <li>
          Formati su otvoreni: kapsula je <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">Nostr događaj</Ext> (
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">NIP-59 „gift wrap"</Ext> za privatnu) čiji je sadržaj{" "}
          <Ext href="https://age-encryption.org">age</Ext> fajl sa <Ext href="https://github.com/drand/tlock-js">tlock</Ext> stavkom. Svaki softver koji
          primenjuje te javne specifikacije može da otvori tvoje kapsule; naš nije neophodan.
        </li>
        <li>
          <b>Preuzmi</b> čuva kapsule na tvom disku, zajedno sa offline otvaračem: jednim HTML fajlom koji ih otvara na tvom računaru, bez
          Vrelis servera, uz potreban drand signal i, za privatnu kapsulu, tvoj ključ. Kompletan izvoz može da se otvori bez vrelis.com ako je odgovarajući
          signal dostupan i sačuvaš potrebne fajlove i ključeve.
        </li>
        <li>
          Ako bi drand stao pre proizvodnje potrebnog budućeg signala, te kapsule mogle bi ostati trajno zaključane. Preuzimanje unapred ne uklanja taj rizik.
          I kapsula čiji je datum otvaranja prošao zahteva svoj tačan verifikovani signal: bez tog signala u arhivi ili dostupnog relay-a, sam datum ne
          omogućava offline otvaranje. Čuvaj kompletne izvoze i potrebne ključeve.
        </li>
      </ul>

      <h3>U šta bi trebalo da poveruješ da bi brava popustila</h3>
      <ul>
        <li>
          Da se većina drand organizacija dogovori da potpiše rundu pre vremena. One su nezavisne, u više država, a lanac je javan, pa bi takav potpis svi
          videli. To je pretpostavka poverenja celog sistema.
        </li>
        <li>
          Da veb strana laže. Strana je kod koji Vrelis šalje tvom browser-u; ko kontroliše taj kod, mogao bi da pročita tekst pre pečaćenja ili tvoj ključ
          posle otključavanja. Zato kapsule mogu da se preuzmu i otvore offline, zato se prikazuju otisci ključeva da ih uporediš van Vrelis-a, i zato je
          posle pilota planirana aplikacija za telefon koja drži ključ van veb strane.
        </li>
        <li>
          Da je današnja kriptografija probijena. Kriptografiju eliptičkih krivih, koju koriste i drand i tvoj ključ, mogli bi da probiju budući{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">kvantni računari</Ext>. Za kapsule koje treba da ostanu zapečaćene decenijama to je stvaran rizik koji pilot ne rešava.
        </li>
      </ul>

      <h2>Kako se ulazi</h2>
      <ol>
        <li>
          Ako je administrator odobrio tvoju adresu, registruj se njome direktno; inače otvori pozivnicu i koristi njenu adresu. Prijavi se tim Google nalogom (Google Vrelis-u kaže samo tvoje ime i email) ili
          lozinkom (mejlom šaljemo link kojim potvrđuješ adresu). Kasnije možeš da dodaš pristupni ključ za prijavu.
        </li>
        <li>
          Vrelis nudi da napravi tvoj <b>ključ</b> u tvom browser-u: tvoj glavni potpis, kojim se potpisuje ono što šalješ i otvaraju privatne kapsule poslate
          tebi. Možeš da preskočiš i napraviš ga kasnije pod Nalog, Ključevi; bez ključa čitaš samo javne kapsule. Pre nego što se pojavi tvoja{" "}
          <b>fraza za oporavak</b> (12 reči koje ponovo grade ključ na bilo kom uređaju), proveri da niko ne vidi i ne snima tvoj ekran.
        </li>
        <li>Zapiši 12 reči na papir. Tri od njih zatim upisuješ, pa se ponovo prijavljuješ da potvrdiš da si to ti: lozinkom ili pristupnim ključem, a za prvi ključ i Google nalogom.</li>
        <li>
          Izaberi PIN (najmanje {l.pinDigits} cifara) i napravi <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">pristupni ključ</Ext> (otisak
          prsta, lice ili PIN uređaja). Zajedno štite ključ na ovom uređaju i
          kopiju koju Vrelis čuva za prelazak na novi uređaj. Ako tvoj browser ne može ovako da koristi pristupni ključ, ključ u tom browser-u ostaje nezaštićen i
          na serveru nema kopije; tada je fraza jedini put do drugog uređaja.
        </li>
      </ol>

      <h2>Tvoja fraza za oporavak</h2>
      <ul>
        <li>Čuvaj je na papiru, na sigurnom mestu. Nemoj da je fotografišeš i nemoj da je čuvaš na računaru ili u menadžeru lozinki.</li>
        <li>
          Ko ima 12 reči, <b>jeste</b> ti: može da potpisuje kao ti i da otvara tvoje privatne kapsule. Ako procure, upotrebi <b>Resetuj ključ</b> pod Nalog,
          Ključevi: ukucaš frazu, ponovo se prijaviš, i ključ je trajno opozvan; niko više ne može da šalje njime niti na njega. Navedeš od kada je možda
          ukraden, i kapsule poslate njime od tada dobijaju oznaku „opozvan ključ“. Zatim praviš nov ključ sa novom frazom.
        </li>
        <li>
          Raniji ključevi mogu da idu u <b>privezak</b>, šifrovan tvojim trenutnim ključem, pa se kapsule poslate na stari ključ i dalje otvaraju bez stare
          fraze. Staru frazu možeš i da čuvaš kod sebe i kasnije je dodaš u privezak.
        </li>
        <li>
          Ako izgubiš uređaj: prijavi se na drugom i upotrebi pristupni ključ i PIN (ako se pristupni ključ sinhronizuje) ili frazu. Ako zaboraviš PIN: upotrebi
          frazu. Posle {l.pinAttempts} pogrešnih PIN-ova kopija na serveru je blokirana; fraza upisuje novu.
        </li>
        <li>
          Ako izgubiš frazu <b>i</b> sve uređaje sa ključem, tvoje privatne kapsule se nikad ne mogu otvoriti, ni od koga. Administrator može samo da ti da nov
          identitet. I opoziv ključa traži frazu, pa ga bez nje opoziva administrator.
        </li>
      </ul>

      <h2>Pisanje kapsule</h2>
      <ul>
        <li>
          <b>Svi u pilotu</b> (javno) ili <b>jedan član</b> (privatno). Javnu kapsulu posle njenog vremena može da pročita svaki član; privatnu samo osoba kojoj
          pišeš, i ti ako sačuvaš kopiju (uključeno je unapred).
        </li>
        <li>
          Svaka kapsula ima <b>naslov</b>. On se nikad ne šifruje: ko vidi kapsulu, odmah ga čita, a vidi ga i naš server.
        </li>
        <li>
          <b>Uvod</b> nije obavezan: do {l.introChars} znakova koji mogu da se pročitaju pre vremena, kad se klikne kapsula. Uvod javne kapsule čita svako,
          i naš server. Uvod privatne čita samo primalac, svojim ključem; server ga ne vidi. Poruka i prilozi u oba slučaja ostaju zaključani.
        </li>
        <li>
          Ko je poslao privatnu kapsulu zapečaćeno je ključem primaoca: primalac to vidi čim otvori kapsulu svojim ključem, mnogo pre njenog vremena.
          Ko ima kapsulu bez tog ključa, vidi samo naslov i vreme. Naš server zna koji nalog je poslao poruku. To kaže samo primaocu, šifrovano njegovim ključem, da aplikacija proveri da ključ koji je potpisao kapsulu pripada tom nalogu; inače umesto imena piše „Nepotvrđen pošiljalac“.
        </li>
        <li>
          <b>Diskretno</b> (privatne kapsule): u listama primaoca piše „Diskretan pošiljalac“ umesto tvog imena dok ne klikne kapsulu, pa niko ko gleda
          preko ramena ne vidi ko mu piše. Primalac i dalje može da vidi da si to ti, pre vremena.
        </li>
        <li>
          Izaberi kada se otvara: za 5 minuta, sat, dan, nedelju, godinu, ili datum do 2125. godine. Stvarno vreme otvaranja vidiš pre slanja; tačno je na
          nekoliko sekundi.
        </li>
        <li>
          Tekst: do {l.publicText} za javnu kapsulu, {l.privateText} za privatnu. Fajlovi: do {l.files} fajlova, ukupno {l.fileBytes} na Free planu ili {l.planFileBytes} sa Legacy ili Start. Fotografije se
          smanjuju i gube metapodatke, uključujući lokaciju.
        </li>
        <li>
          <b>Zapečati i pošalji</b> jednom traži PIN i pristupni ključ, da potpiše kapsulu. Posle toga niko ne može da je promeni ni da je pročita pre njenog
          vremena.
        </li>
        <li>
          Pre nego što nekome pišeš privatno, proveri njegov ključ: Piši pokazuje otisak ključa (16 znakova). Ako se ključ promenio otkad ga je ovaj browser
          poslednji put video, Piši te upozorava i čeka da sa tom osobom, van Vrelis-a, potvrdiš da je novi ključ zaista njen.
        </li>
      </ul>

      <h2>Čitanje</h2>
      <ul>
        <li>
          <b>Javno</b>: javne kapsule. <b>Primljeno</b>: privatne kapsule upućene tebi. <b>Poslato</b>: tvoje. <b>Čekam</b>: javne kapsule čije otvaranje čekaš.
          „Prati” autora da vidiš samo njegove kapsule.
        </li>
        <li>
          <b>Sakrij</b> člana: njegove javne kapsule nestaju iz tvog Javno, na svim uređajima; za njega se ništa ne menja. <b>Blokiraj</b> člana: više
          ne može da ti ubaci privatne kapsule u Primljeno (Nova poruka mu to kaže pre slanja); i dalje možeš da ga pratiš i čitaš njegove javne
          kapsule. Njegove ranije kapsule prelaze u Primljeno, Blokirani, i dalje se otvaraju. Oba su u meniju osobe i na stranici Ljudi, uz to ko te prati.
          Vrelis garantuje samo za ono što prima i dostavlja: niko ne može da spreči nekog da šifruje za tvoj javni ključ van Vrelisa.
        </li>
        <li>
          Privatna kapsula ne pokazuje ništa dok je ne otvoriš: <b>Otvori</b> traži PIN, pa pokazuje ko je poslao i kada se otvara, a tekst kad dođe to vreme.
        </li>
        <li>
          Tvoj ključ ostaje zaključan; svaka radnja kojoj treba traži PIN. Za čitanje više kapsula zaredom upotrebi <b>Otključaj na … min</b> u traci na vrhu.{" "}
          <b>Zaključaj</b> odmah sakriva sve privatno; isto radi i odlazak sa stranice.
        </li>
        <li>
          Pod <a href="/account">Nalog</a>, Privatnost: „Prikaži pošiljaoce u listama tek kad kliknem kapsulu“ tretira svaku privatnu kapsulu kao diskretnu,
          na svim uređajima.
        </li>
        <li>
          Liste prikazuju po 50 kapsula; <b>Prikaži još</b> dodaje sledeće. Vrelis ih filtrira i sortira (po vremenu otvaranja ili po tome kada ih je
          primio), pa filter ili grupa šalju Vrelis-u id-jeve članova koje si izabrao, nikad grupu ni njeno ime. Filter Primljenog po pošiljaocu traži
          tvoj ključ, izostavlja diskretne kapsule i isključen je dok se pošiljaoci vide tek posle klika.
        </li>
        <li>
          <b>Pretraga</b> gleda naslove, uvod javnih kapsula, ko je napisao javnu kapsulu i kome si slao. Nikad ne gleda otvoren tekst (ima ga samo tvoj
          uređaj) niti ko ti je poslao privatnu kapsulu.
        </li>
      </ul>

      <h2>Povratnica, CC i Prepiska</h2>
      <ul>
        <li>
          Privatna poruka može da nosi <b>povratnicu</b>: od svakog primaoca tražiš jedan privatan odgovor nazad, zapečaćen tvojim ključem.
          Primalac može da odgovori i kad te ne prati. Biraš da li se svi odgovori otvaraju u vreme koje odrediš (posle tog vremena odgovor se
          ne prima) ili u vreme koje primalac izabere. Kapsulu i odgovor plaćaš unapred: 2 marke po primaocu umesto 1. Prvi odgovor primaoca ne košta njega ništa; drugi ga košta jednu marku, a ti vidiš oba.
        </li>
        <li>
          <b>CC</b>: privatna poruka jednom članu može odjednom da ide i drugim članovima, a svi sa spiska vide ko je još primio. Spisak je
          zapečaćen unutar poruke sa potpisom pošiljaoca; naš server ga ne vidi. Grupno slanje bez CC ostaje slepo: niko ne vidi ko je još dobio.
        </li>
        <li>
          Poruke sa povratnicom i odgovori na njih stoje na strani <b>Prepiska</b>, ne u Primljeno ili Poslato: ispod svakog zahteva vidiš
          odgovore svakog primaoca po redu, zaključane do svog vremena, i ko je koji platio. Odgovor ide samo pošiljaocu.
        </li>
      </ul>

      <h2>Čuvanje kapsula bez Vrelis-a</h2>
      <ul>
        <li>
          <b>Preuzmi</b> na kapsuli čuva je kao fajl; sa prilozima je to .zip sa fajlovima i offline otvaračem. Izaberi više kapsula i upotrebi{" "}
          <b>Preuzmi izabrano</b> za jednu arhivu („Izaberi sve prikazane” uzima prikazane kapsule).
        </li>
        <li>
          Offline otvarač je jedan HTML fajl (Account, ili u .zip arhivi). Otvara tvoje kapsule na tvom računaru, privatne uz frazu. Nikad ne kontaktira
          vrelis.com; samo pita javni drand vremenski servis za signal otvaranja, ako ga fajl još nema.
        </li>
        <li>„Uključi otvoren tekst i fajlove” stavlja otvoren tekst i fajlove u preuzimanje nešifrovane: svako ko ima fajl može da ih pročita.</li>
      </ul>

      <h2>Ovaj uređaj</h2>
      <ul>
        <li>
          <b>Zaključaj</b>: uklanja ključ iz memorije. <b>Odjavi se</b>: završava ovu sesiju; ključ ostaje na ovom uređaju. <b>Odjavi se svuda</b>: završava
          tvoje sesije na svim uređajima. <b>Zaboravi ovaj uređaj</b>: briše tvoj ključ i sve što je Vrelis sačuvao u ovom browser-u.
        </li>
        <li>Prijava traje {l.sessionDays} dana.</li>
      </ul>

      <h2>Šta Vrelis vidi, a šta ne</h2>
      <ul>
        <li>
          <b>Vrelis ne može da pročita</b> kapsulu pre njenog vremena, ni privatnu kapsulu ikada (nema ključ za nju), ni tvoj ključ, PIN ili frazu.
        </li>
        <li>
          <b>Vrelis vidi</b>: tvoje ime i email (email vide vlasnik i administratori), ko šalje kapsulu i kome, kada je poslata, kada se javna kapsula otvara, veličine i
          to da kapsula ima fajlove. Posle svog vremena javnu kapsulu može da pročita svaki član, pa i Vrelis.
        </li>
        <li>
          Potpis dokazuje kojim ključem je kapsula napisana. Da taj ključ pripada nekoj osobi, oslanja se na spisak članova i na ime koje je pošiljalac stavio u
          kapsulu. Proveri otiske ključeva sa ljudima kojima pišeš privatno.
        </li>
        <li>
          Privezak i kopije tvog ključa čuvaju se samo šifrovani; Vrelis ne može da ih otvori. Kapsula sa oznakom „opozvan ključ“ potpisana je ključem
          za koji je vlasnik kasnije javio da je ukraden.
        </li>
      </ul>

      <h2>Planovi i marke</h2>
      <ul>
        <li>
          Jedna <b>marka</b> plaća jednu isporuku jednom primaocu. Tvoja kopija je besplatna. Free daje {l.freeStamps} maraka mesečno, {l.freeStorage} prostora i {l.fileBytes} fajlova po poruci; Legacy
          ({l.legacyStamps} maraka mesečno, {l.planStorage}, {l.planFileBytes}) dobili su prvi članovi; Start bi davao {l.planStamps} maraka mesečno. Marke jednog meseca se ne prenose i vraćaju se prvog dana narednog
          meseca (UTC). <b>Obračun</b> pokazuje šta imaš, šta je ostalo i šta je planirano.
        </li>
        <li>
          Za sada se ništa ne naplaćuje i ništa se ne može kupiti: planovi su pregled, a admin uključuje zahtev ručno. Šta god se desilo sa planom ili markama, već poslato ostaje čitljivo tebi i onima kojima je poslato.
        </li>
        <li>
          Kopija za tebe je opciona i podrazumevano uključena. Ako je isključiš, tu poruku može da pročita samo primalac: sam je nećeš moći pročitati, čak ni kad se otvori.
        </li>
      </ul>

      <h2>Ograničenja pilota</h2>
      <ul>
        <li>
          Najviše {l.daily} isporuka dnevno (tvoja kopija je besplatna; kapsula grupi se računa po jedna za svakog člana). Otpremanje: {l.uploadDaily} i {l.unusedFiles} neiskorišćenih fajlova za 24
          sata; fajlovi koji za 24 sata ne uđu u kapsulu se brišu.
        </li>
        <li>Prijava Google nalogom, lozinkom ili pristupnim ključem. Nema plaćanja ni javne mreže: kapsule ostaju unutar pilota.</li>
        <li>
          Pozivnica za adresu koja već ima nalog tom članu šalje zahtev za povezivanje; ako prihvati, pratite jedno drugo. Tvoj ekran izgleda isto u
          oba slučaja, pa niko pozivnicama ne može da proveri ko je član.
        </li>
      </ul>

      <h2>Za programere</h2>
      <p>
        Servisi mogu da čitaju i šalju kapsule preko <a href="/developers">Vrelis API-ja</a>. Servis dobija sopstveni nalog servisnom pozivnicom od
        administratora; svima ostalima je običan član. Sam pravi svoj ključ i sam potpisuje svaku kapsulu; njegovi API tokeni mogu da čitaju i šalju, ali ne
        mogu da upravljaju ključevima ni načinima prijave. Servis možeš da blokiraš ili sakriješ kao bilo koga.
      </p>

      <h2>Šta molimo članove pilota</h2>
      <ol>
        <li>Pošalji jednu javnu kapsulu koja se otvara za sat i jednu privatnu kapsulu drugom članu koja se otvara za dan.</li>
        <li>Pročitaj javne kapsule kad se otvore, i pokušaj da pročitaš jednu ranije: ne bi trebalo da uspe.</li>
        <li>Jedno od vas: postavi ključ na drugom uređaju i tamo pročitaj Primljeno.</li>
        <li>Jedno od vas: sakrij drugog člana i proveri da njegove kapsule nestaju samo iz tvog Javno.</li>
      </ol>

      <h2>Problemi</h2>
      <p>
        Upotrebi <b>Prijavi problem</b> na dnu svake stranice, ili piši na {link}. Opiši korake, šta je trebalo da se desi i šta se zapravo desilo; snimak ekrana pomaže.
        Usluga, njena ograničenja i odgovornosti opisani su u <a href="/terms">Uslovima korišćenja</a>.
      </p>
    </>
  );
}
